import { useRef, useState } from 'react';

import { ApiError, download } from '../api/client';
import {
  useImportArchive,
  useMaintenanceSummary,
  useRefillQueue,
  useResetFailedTasks,
  useResetUnmatchedTasks,
} from '../api/queries';
import { DIVERGE, STATUS } from '../charts/palette';
import { Card } from '../components/Card';
import { Failed, Loading } from '../components/States';
import { StatTile } from '../components/StatTile';
import { count, when } from '../utils/format';

export function SettingsPage() {
  const summary = useMaintenanceSummary();
  const reset = useResetFailedTasks();
  const rescanUnmatched = useResetUnmatchedTasks();
  const refill = useRefillQueue();
  const importArchive = useImportArchive();
  const filePicker = useRef<HTMLInputElement>(null);

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [pending, setPending] = useState<File | null>(null);

  async function runExport() {
    setExportError(null);
    setExporting(true);

    try {
      await download('/maintenance/export', 'price-radar.zip');
    } catch (cause) {
      setExportError(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setExporting(false);
    }
  }

  function confirmImport() {
    if (pending === null) {
      return;
    }

    importArchive.mutate(pending, { onSettled: () => setPending(null) });
  }

  return (
    <div className="stack">
      <header className="page__head">
        <div>
          <h1 className="page__title">Settings</h1>
          <p className="page__lede">
            Move this deployment&apos;s data somewhere else, and undo queue states the crawler cannot undo
            itself.
          </p>
        </div>
      </header>

      {summary.isError ? (
        <Failed error={summary.error} />
      ) : summary.isLoading ? (
        <Loading label="Reading the database…" />
      ) : (
        summary.data && (
          <div className="grid grid--tiles">
            <StatTile label="Rows in total" value={count(summary.data.totalRows)} foot={`across ${summary.data.tables.length} tables`} />
            <StatTile label="Database" value={summary.data.database} foot="what an archive would be taken from" />
            <StatTile
              label="No Heureka match"
              value={count(summary.data.unmatchedProducts)}
              foot={
                summary.data.unmatchedProducts > 0
                  ? 'scanned, but nothing usable came back'
                  : 'every product is matched'
              }
              accent={summary.data.unmatchedProducts > 0 ? STATUS.warning : STATUS.good}
            />
            <StatTile
              label="Failed crawl tasks"
              value={count(summary.data.failedTasks)}
              foot={summary.data.failedTasks > 0 ? 'waiting out the rescan interval' : 'nothing stuck'}
              accent={summary.data.failedTasks > 0 ? STATUS.warning : STATUS.good}
            />
          </div>
        )
      )}

      <div className="grid grid--halves">
        <Card
          title="Export everything"
          note="Every table as JSON inside one zip. Readable, and independent of the MySQL version at either end."
        >
          <p className="card__text">
            Use this to migrate to another host: export here, then import on the new deployment.
          </p>

          {exportError && (
            <div className="login__error" role="alert" style={{ marginBottom: 'var(--space-3)' }}>
              {exportError}
            </div>
          )}

          <div className="card__actions">
            <button type="button" className="button button--primary" onClick={runExport} disabled={exporting}>
              {exporting ? 'Preparing the archive…' : 'Download archive'}
            </button>
          </div>
        </Card>

        <Card
          title="Import an archive"
          note="Replaces every table with the archive's contents, in one transaction"
        >
          <p className="card__text">
            This <strong>deletes everything currently stored</strong> and puts the archive in its place. The
            file is checked first, so a wrong zip is refused rather than destructive.
          </p>

          <input
            ref={filePicker}
            type="file"
            accept=".zip,application/zip"
            style={{ display: 'none' }}
            onChange={(event) => setPending(event.target.files?.[0] ?? null)}
          />

          {pending === null ? (
            <div className="card__actions">
              <button type="button" className="button" onClick={() => filePicker.current?.click()}>
                Choose a zip…
              </button>
            </div>
          ) : (
            <div className="stack" style={{ gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
              <div className="banner">
                <span aria-hidden="true">⚠</span>
                <div>
                  <strong>Replace everything with {pending.name}?</strong>
                  <div className="muted" style={{ marginTop: 4 }}>
                    {summary.data ? `${count(summary.data.totalRows)} rows will be deleted first.` : ''} This
                    cannot be undone — export first if you have not.
                  </div>
                </div>
              </div>

              <div className="row">
                <button
                  type="button"
                  className="button"
                  style={{ borderColor: DIVERGE.high, color: DIVERGE.high }}
                  onClick={confirmImport}
                  disabled={importArchive.isPending}
                >
                  {importArchive.isPending ? 'Importing…' : 'Yes, replace everything'}
                </button>
                <button type="button" className="button" onClick={() => setPending(null)} disabled={importArchive.isPending}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {importArchive.isError && (
            <div className="login__error" role="alert" style={{ marginTop: 'var(--space-3)' }}>
              {extractMessage(importArchive.error)}
            </div>
          )}

          {importArchive.isSuccess && importArchive.data && (
            <div className="state" style={{ marginTop: 'var(--space-3)' }}>
              Restored {count(importArchive.data.totalRows)} rows from an archive taken{' '}
              {when(importArchive.data.createdAt)}.
            </div>
          )}
        </Card>
      </div>

      <Card
        title="Refill the crawl queue"
        note="Enqueue everything that is due now, instead of waiting for the hourly refill"
      >
        <p className="card__text">
          The scheduler refills on boot and then once an hour. A deployment whose catalogue arrived after the
          scheduler started therefore has an empty queue until the next hour boundary — press this after a first
          feed import rather than waiting. Safe to repeat: a product already queued is not queued twice, and one
          still inside its rescan window is left alone.
        </p>

        <div className="card__actions">
          <button type="button" className="button" onClick={() => refill.mutate()} disabled={refill.isPending}>
            {refill.isPending ? 'Refilling…' : 'Refill queue now'}
          </button>
        </div>

        {refill.isError && (
          <div className="login__error" role="alert" style={{ marginTop: 'var(--space-3)' }}>
            {extractMessage(refill.error)}
          </div>
        )}

        {refill.isSuccess && refill.data && (
          <div className="state" style={{ marginTop: 'var(--space-3)' }}>
            {refill.data.queued === 0
              ? `Nothing to queue — all ${count(refill.data.activeProducts)} products are inside their rescan window.`
              : `Queued ${count(refill.data.queued)} of ${count(refill.data.activeProducts)} products.`}
          </div>
        )}
      </Card>

      <Card
        title="Rescan products with no match"
        note="For products Heureka did not give usable numbers for, without waiting out the rescan window"
      >
        <p className="card__text">
          A scan that ends in <strong>not found</strong> or <strong>ambiguous</strong> counts as finished, so
          the queue leaves that product alone for the full two weeks. That is right when the product genuinely
          is not listed, and wrong straight after the matcher has been changed — press this to try them all
          again on the next tick.
        </p>

        <div className="card__actions">
          <button
            type="button"
            className="button"
            onClick={() => rescanUnmatched.mutate()}
            disabled={rescanUnmatched.isPending || summary.data?.unmatchedProducts === 0}
          >
            {rescanUnmatched.isPending
              ? 'Requeueing…'
              : `Rescan ${count(summary.data?.unmatchedProducts ?? 0)} unmatched product(s)`}
          </button>
          {summary.data?.unmatchedProducts === 0 && (
            <span className="muted">Every product has a match.</span>
          )}
        </div>

        {rescanUnmatched.isError && (
          <div className="login__error" role="alert" style={{ marginTop: 'var(--space-3)' }}>
            {extractMessage(rescanUnmatched.error)}
          </div>
        )}

        {rescanUnmatched.isSuccess && rescanUnmatched.data && (
          <div className="state" style={{ marginTop: 'var(--space-3)' }}>
            Requeued {count(rescanUnmatched.data.requeued)} product(s); they will be scanned over the next few
            ticks.
          </div>
        )}
      </Card>

      <Card
        title="Requeue failed crawl tasks"
        note="Moves every failed task back to pending so the next tick picks it up"
      >
        <p className="card__text">
          The queue treats a failed task like a finished one and waits the full rescan interval before trying
          again — right when a product genuinely has no match, wrong when the failures came from a transport
          that has since been fixed. Two weeks is a long time to wait for a mistake to expire.
        </p>

        <div className="card__actions">
          <button
            type="button"
            className="button"
            onClick={() => reset.mutate()}
            disabled={reset.isPending || summary.data?.failedTasks === 0}
          >
            {reset.isPending ? 'Requeueing…' : 'Requeue failed tasks'}
          </button>
          {summary.data?.failedTasks === 0 && <span className="muted">Nothing is failed right now.</span>}
        </div>

        {reset.isError && (
          <div className="login__error" role="alert" style={{ marginTop: 'var(--space-3)' }}>
            {extractMessage(reset.error)}
          </div>
        )}

        {reset.isSuccess && reset.data && (
          <div className="state" style={{ marginTop: 'var(--space-3)' }}>
            Requeued {count(reset.data.requeued)} task(s).
          </div>
        )}
      </Card>

      {summary.data && (
        <Card title="What an archive contains" note="In the order a restore applies them, parents before children">
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Table</th>
                  <th className="num">Rows</th>
                </tr>
              </thead>
              <tbody>
                {summary.data.tables.map((table) => (
                  <tr key={table.table}>
                    <td>{table.table}</td>
                    <td className="num">{count(table.rows)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

/** Nest wraps its errors in JSON; show the sentence, not the envelope. */
function extractMessage(error: unknown): string {
  const raw = error instanceof ApiError ? error.message : String(error);

  try {
    const parsed = JSON.parse(raw) as { message?: string | string[] };

    return Array.isArray(parsed.message) ? parsed.message.join(', ') : (parsed.message ?? raw);
  } catch {
    return raw;
  }
}
